import { MouseEventHandler, useEffect, useRef, useState } from "react";
import ImgBookmarked from "@assets/icons/bookmarked.svg";
import ArticleDataType from "@customTypes/articleData.type";
import { articleFallbackImageBaseUrl } from "@components/pages/feedPage/constants";
import LogoDark from "@assets/icons/logoDark.svg";
import LogoLight from "@assets/icons/logoLight.svg";
import ThemedImage from "@components/ui-components/ThemedImage";
import { useTheme } from "next-themes";
import Link from "next/link";

type Props = {
  showBookmark?: boolean;
  loading?: boolean;
  articleData: ArticleDataType;
  handleRemoveBookmark?: Function;
  groupName?: string;
  onCardClick: MouseEventHandler;
  observer?: any;
  titleHref: string;
};

const ArticleMiniCard = ({
  showBookmark = false,
  articleData,
  loading,
  handleRemoveBookmark,
  groupName,
  onCardClick,
  observer,
  titleHref
}: Props) => {
  const [mounted, setMounted] = useState(false);
  const { resolvedTheme } = useTheme();
  const articleCardImageRef = useRef(null);

  useEffect(() => {
    setMounted(true);
  }, []);
  const onBookmarkClick = (e) => {
    e.stopPropagation();
    handleRemoveBookmark({
      bookmarkId: articleData.id,
      groupName,
      remove: true
    });
  };

  const str = `${
    articleData ? (articleData?.summary || "").split("</li>")[1].split("<li>")[1] : ""
  }`;
  useEffect(() => {
    if (observer && articleCardImageRef && articleCardImageRef.current && mounted) {
      observer.observe(articleCardImageRef.current);
      return () => {
        observer.destroy();
      };
    }
  }, [observer, resolvedTheme]);
  return (
    <div
      itemScope
      itemType="https://schema.org/SiteNavigationElement"
      className="mr-3 w-56 shrink-0 cursor-pointer"
      onClick={onCardClick}
      ref={articleCardImageRef}
    >
      <div className="h-max-[182px] w-max-[222px] flex h-[182px] items-center justify-center rounded-md bg-N200 dark:bg-DN200">
        <ThemedImage
          mounted={mounted}
          resolvedTheme={resolvedTheme}
          darkSrc={LogoDark}
          lightSrc={LogoLight}
          // ref={articleCardImageRef}
          width={40}
          height={40}
          alt="article-image"
          dataSrc={articleData.mediaURL}
          className={"h-10 w-10 rounded object-contain"}
          onError={({ currentTarget }) => {
            if (
              currentTarget.src !== `${articleFallbackImageBaseUrl}${articleData.channelIds[0]}.png`
            ) {
              currentTarget.src = `${articleFallbackImageBaseUrl}${articleData.channelIds[0]}.png`;
            } else {
              const src = mounted ? (resolvedTheme === "dark" ? LogoDark : LogoLight) : LogoLight;
              currentTarget.src = src;
              currentTarget.classList.add("h-10", "w-10");
              currentTarget.classList.remove("h-full", "w-full", "object-cover");
            }
            currentTarget.onerror = null; // prevents looping
          }}
        />
        {/* <img
          data-src={articleData.mediaURL}
          width="100%"
          height="100%"
          alt="logo"
          className="h-full w-full rounded object-cover"
          onError={({ currentTarget }) => {
            if (
              currentTarget.src !== `${articleFallbackImageBaseUrl}${articleData.channelIds[0]}.png`
            ) {
              currentTarget.src = `${articleFallbackImageBaseUrl}${articleData.channelIds[0]}.png`;
            }
          }}
        /> */}
      </div>

      {/* <p className="mt-2.5 h-[4.5rem] max-h-[4.5rem] overflow-hidden text-readingTextLarge font-bold leading-readingTextLarge text-N900 line-clamp-3 dark:text-DN900">
        {loading ? (
          <div className="h-[90%] w-full animate-pulse bg-N200 dark:bg-DN200" />
        ) : (
          articleData.title
        )}
      </p> */}
      {loading ? (
        <div className="h-[90%] w-full animate-pulse bg-N200 dark:bg-DN200" />
      ) : (
        <Link href={titleHref} passHref prefetch={false}>
          <a
            itemProp="url"
            title={articleData.title}
            className="mt-2.5 h-[4.5rem] max-h-[4.5rem] overflow-hidden text-readingTextLarge font-bold leading-readingTextLarge text-N900 line-clamp-3 dark:text-DN900"
          >
            <span itemProp="name" className="mr-2">
              {articleData.title}
            </span>
          </a>
        </Link>
      )}
      <p
        itemProp="description"
        className="mt-1 h-[48px] text-readingTextSmall font-regular leading-readingTextSmall text-N500 line-clamp-3 dark:text-DN500"
      >
        {str}
      </p>
      <div className="mt-3 flex items-center justify-between">
        <p
          itemProp="publisher"
          itemScope
          itemType="http://schema.org/Organization"
          className="text-readingTextSmall font-regular leading-readingTextSmall text-N500 dark:text-DN500"
        >
          {loading ? (
            <div className="h-5 w-[160px] animate-pulse bg-N200 dark:bg-DN200" />
          ) : (
            <span itemProp="name">{`${articleData.sourceName}`}</span>
          )}
        </p>
        {showBookmark && !loading && (
          <img onClick={onBookmarkClick} src={ImgBookmarked} alt="bookmarked" />
        )}
      </div>
    </div>
  );
};

export default ArticleMiniCard;
